import React, { useCallback, useEffect, useMemo, useState } from "react";
import { networkService } from "../../config/axios";
import "./PersonalInsightsPage.css";

const FILTERS = [
  { value: "awaiting_review", label: "Waiting for review" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
  { value: "", label: "All" },
];

const STATUS_LABELS = {
  pending: "Waiting for the recipient",
  generating: "Being written",
  awaiting_review: "Waiting for review",
  approved: "Approved, sending",
  rejected: "Rejected",
  sent: "Sent",
  failed: "Failed",
};

const TRIGGER_LABELS = {
  end_of_route: "Written at the end of their route",
  page: "Written when they reached the trigger page",
  inactivity: "Written after 30 minutes without activity",
};

const SECTIONS = [
  { key: "means", label: "What this means for you", max: 3 },
  { key: "closer", label: "Worth a closer look", max: 5 },
  { key: "share", label: "Share with your team", max: 6 },
];

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "");

const toDraft = (content = {}) => ({
  top_sections: content.top_sections || "",
  key_responses: content.key_responses || "",
  means: (content.means || []).join("\n"),
  closer: (content.closer || []).join("\n"),
  share: (content.share || []).join("\n"),
});

const fromDraft = (draft) => ({
  top_sections: draft.top_sections,
  key_responses: draft.key_responses,
  means: draft.means.split("\n"),
  closer: draft.closer.split("\n"),
  share: draft.share.split("\n"),
});

const recipientName = (guide) =>
  [guide?.recipient?.first_name, guide?.recipient?.last_name].filter(Boolean).join(" ") || guide?.email;

const PersonalInsightsPage = () => {
  const initialGuideId = useMemo(() => Number(new URLSearchParams(window.location.search).get("guide")) || null, []);
  const [filter, setFilter] = useState(initialGuideId ? "" : "awaiting_review");
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(initialGuideId);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmReject, setConfirmReject] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = filter ? `?status=${filter}` : "";
      const { data } = await networkService(`personal_insights_guides.json${query}`, "GET");
      setGuides(data?.guides || []);
    } catch (error) {
      setGuides([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = guides.find((guide) => guide.id === selectedId) || null;
  const reviewable = selected?.status === "awaiting_review";

  useEffect(() => {
    setDraft(selected ? toDraft(selected.content) : null);
    setConfirmReject(false);
    setMessage(null);
  }, [selected?.id, selected?.status]);

  const replaceGuide = (updated) => setGuides((current) => current.map((guide) => (guide.id === updated.id ? updated : guide)));

  const run = async (action) => {
    if (!selected) return;
    setBusy(true);
    setMessage(null);
    try {
      const changed = JSON.stringify(draft) !== JSON.stringify(toDraft(selected.content));
      if (action === "save" || (action === "approve" && changed)) {
        const { data } = await networkService(`personal_insights_guides/${selected.id}.json`, "PATCH", {
          content: fromDraft(draft),
        });
        replaceGuide(data);
      }
      if (action === "approve" || action === "reject") {
        const { data } = await networkService(`personal_insights_guides/${selected.id}/${action}.json`, "POST", {});
        replaceGuide(data);
      }
      setMessage({
        type: "success",
        text: action === "approve" ? "Approved. The guide is being sent." : action === "reject" ? "Rejected. It will not be sent." : "Changes saved.",
      });
    } catch (error) {
      setMessage({ type: "danger", text: error?.response?.data?.error || "Something went wrong. Please try again." });
    } finally {
      setBusy(false);
      setConfirmReject(false);
    }
  };

  return (
    <div className="kz-pi-page">
      <div className="kz-pi-page__filters">
        {FILTERS.map((option) => (
          <button
            key={option.value || "all"}
            type="button"
            className={`kz-pi-page__filter ${filter === option.value ? "kz-pi-page__filter--active" : ""}`}
            onClick={() => setFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <div className="kz-pi-page__body">
        <div className="kz-pi-page__list">
          {loading && <p className="text-muted p-3">Loading…</p>}
          {!loading && guides.length === 0 && <p className="text-muted p-3">No guides here.</p>}
          {guides.map((guide) => (
            <button
              key={guide.id}
              type="button"
              className={`kz-pi-page__item ${guide.id === selectedId ? "kz-pi-page__item--active" : ""}`}
              onClick={() => setSelectedId(guide.id)}
            >
              <strong>{recipientName(guide)}</strong>
              <span>{guide.kdf?.title}</span>
              <span className="kz-pi-page__meta">
                {STATUS_LABELS[guide.status] || guide.status} · {formatDate(guide.created_at)}
              </span>
            </button>
          ))}
        </div>
        <div className="kz-pi-page__detail">
          {!selected && <p className="text-muted">Choose a guide to see it.</p>}
          {selected && draft && (
            <>
              <h5 className="mb-1">{recipientName(selected)}</h5>
              <p className="kz-pi-page__meta mb-3">
                {selected.recipient?.email} · {selected.kdf?.title} · LaserLINK {selected.laserlink?.name}
                {selected.reference_number ? ` · Ref ${selected.reference_number}` : ""} · {STATUS_LABELS[selected.status] || selected.status}
                {selected.sent_at ? ` ${formatDate(selected.sent_at)}` : ""}
                {selected.locale ? ` · ${selected.locale}` : ""}
                {TRIGGER_LABELS[selected.trigger_reason] ? ` · ${TRIGGER_LABELS[selected.trigger_reason]}` : ""}
              </p>
              {selected.trigger_reason === "inactivity" && (
                <div className="alert alert-info small">
                  This recipient stopped reading before the end of their route, so the guide covers only what they saw.
                </div>
              )}
              {selected.status === "failed" && selected.last_error && (
                <div className="alert alert-danger small">Last error: {selected.last_error}</div>
              )}
              <label className="kz-pi-page__label">What they looked at most closely</label>
              <input
                className="form-control mb-2"
                dir="auto"
                value={draft.top_sections}
                disabled={!reviewable}
                onChange={(e) => setDraft({ ...draft, top_sections: e.target.value })}
              />
              <label className="kz-pi-page__label">Their most telling responses</label>
              <input
                className="form-control mb-3"
                dir="auto"
                value={draft.key_responses}
                disabled={!reviewable}
                onChange={(e) => setDraft({ ...draft, key_responses: e.target.value })}
              />
              {SECTIONS.map((section) => (
                <div key={section.key} className="mb-3">
                  <label className="kz-pi-page__label">
                    {section.label} <span className="kz-pi-page__meta">(one line per point, up to {section.max})</span>
                  </label>
                  <textarea
                    className="form-control"
                    dir="auto"
                    rows={Math.max(3, draft[section.key].split("\n").length)}
                    value={draft[section.key]}
                    disabled={!reviewable}
                    onChange={(e) => setDraft({ ...draft, [section.key]: e.target.value })}
                  />
                </div>
              ))}
              <p className="kz-pi-page__meta">
                Sections left empty are left out of the email. The recipient's name, greeting, footer and links are added
                when the email is sent.
              </p>
              {message && <div className={`alert alert-${message.type} small`}>{message.text}</div>}
              {reviewable && (
                <div className="d-flex gap-2 flex-wrap">
                  <button type="button" className="btn kz-pi-page__approve" disabled={busy} onClick={() => run("approve")}>
                    Approve and send
                  </button>
                  <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => run("save")}>
                    Save changes
                  </button>
                  {confirmReject ? (
                    <>
                      <span className="align-self-center small">Reject for good? It will never be sent.</span>
                      <button type="button" className="btn btn-danger" disabled={busy} onClick={() => run("reject")}>
                        Reject
                      </button>
                      <button type="button" className="btn btn-link" onClick={() => setConfirmReject(false)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button type="button" className="btn btn-outline-danger" disabled={busy} onClick={() => setConfirmReject(true)}>
                      Reject
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalInsightsPage;
